import React, {
  CSSProperties,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
} from "react";
import {
  AdvancedSettings,
  AmountType,
  BotData,
  BotType,
  ClientData,
  ConfigurationItem,
  DefaultOrder,
  LongShort,
  MarginTypes,
  OnlyHelperTooltipProps,
  OrderBaseline,
  StopType,
  StrategyData,
  SubGroup,
  FormTrailingStopData,
  StrippedBotData,
  NumberOption,
  CustomStrategyData,
  OverrideFieldType,
  MarketplaceSubGroup,
  GroupData,
  BlacklistedOperationHours,
} from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  channelValue,
  empty,
  off,
} from "@shared/features/bots/BotForm/common/constants";
import { TradeFormValues } from "@shared/features/tradingTerminal/common/types";
import { CoinGroupSignalsData } from "@shared/features/onboarding/types";

export enum StrategyTabs {
  CUSTOM = "custom",
  DCA = "dca",
  BUILT_IN = "built_in",
}

export enum Steps {
  GENERAL = "General",
  ENTRIES = "Entries",
  TAKE_PROFITS = "Take-Profits",
  STOP = "Stop",
  ADVANCED = "Advanced",
}

export enum ChartTabs {
  CHART = "Chart",
  BACKTESTING_RESULTS = "Backtesting Results",
}

export enum Error {
  GENERAL = 1,
  ENTRY_STRATEGY = 2,
  DEFAULT_STOP = 3,
  DEFAULT_TAKE_PROFIT = 4,
  TAKE_PROFIT_STRATEGY = 5,
  MIN_ENTRY_ORDER_AMOUNT = 6,
  ACTIVE_ENTRIES_AMOUNT = 7,
  GENERAL_AMOUNT = 8,
  ENTRIES_RATIO_SUM = 9,
  INSUFFICIENT_AMOUNT = 10,
  INVALID_LEVERAGE = 11,
  ENTRY_PRICES = 12,
  TAKE_PROFIT_PRICES = 13,
  GRIDS_DATA = 14,
  CONSECUTIVE_GRIDS_PRICE_DISTANCE = 15,
  GENERAL_ADVANCED,
  GENERAL_STOP,
  SUB_GROUP,
  BOT_NAME,
  ALTERNATIVE_USD_PAIRS,
}

export enum ToggleField {
  ENTRIES = "entries",
  TAKE_PROFITS = "takeProfits",
  TAKE_PROFIT_STRATEGY = "takeProfitStrategy",
  TAKE_PROFIT_PRICE_STRATEGY = "takeProfitPriceStrategy",
  TAKE_PROFIT_RATIOS = "takeProfitRatios",
  TAKE_PROFIT_NUM_ORDERS = "takeProfitNumOrders",
  TAKE_PROFIT_NUM_ACTIVE_ORDERS = "takeProfitNumActiveOrders",
  TRAILING_TAKE_PROFIT = "trailingTakeProfit",
  TRAILING_ENTRY = "trailingEntry",
  ENTRY_PRICE_STRATEGY = "entryPriceStrategy",
  ENTRY_RATIOS = "entryRatios",
  ENTRY_NUM_ORDERS = "entryNumOrders",
  ENTRY_NUM_ACTIVE_ORDERS = "entryNumActiveOrders",
  STOP = "stop",
  TRAILING_STOP = "trailingStop",
  STOP_TIMEOUT = "stopTimeout",
  ENTRY_GRACE_PERCENT = "entryGracePercent",
  FIRST_ENTRY = "firstEntry",
  CLOSE_ON_TP_BEFORE_ENTRY = "closeOnTpBeforeEntry",
  MARGIN = "margin",
  TAKE_PROFIT_DYNAMIC = "takeProfitDynamic",
  SKIP_WITHOUT_STOP_LOSS = "skipWithoutStopLoss",
  AMOUNT_PER_TRADE = "amountPerTrade",
  ALTERNATIVE_USD_PAIRS = "alternativeUsdPairs",
  TAKE_PROFIT_GRACE_PERCENT = "takeProfitGracePercent",
  OPERATION_HOURS = "operationHours",
}

export interface StrategyOrder {
  price: number;
  ratio: number;
  amount: number;
  isValid: boolean;
  minAmount: number;
  btcAmount: number;
  usdAmount: number;
  availableAmountWarning?: boolean;
  isPending?: boolean;
  isImmediate?: boolean;
  isActive?: boolean;
}

export type SharedStrategyOrder = Pick<
  StrategyOrder,
  "price" | "ratio" | "minAmount"
>;

export type SharedOrderData = {
  price: number;
  ratio: number;
};

export interface InitialBotAmountData {
  initialUsdAmount: number;
  initialMainCoinAmount: number;
  activeUsdAmount: number;
  activeMainCoinAmount: number;
}

export enum BotFormType {
  CREATE = "create",
  BULK_CREATE = "bulk_create",
  EDIT = "edit",
  DUPLICATE = "duplicate",
  COPY_PUBLIC = "copy_public",
  BULK_EDIT = "bulk_edit",
}

export interface ExchangeSymbolAmountData {
  account: ClientData;
  symbol: string;
  mainCoin: string;
  mainCoinUSDAmount: number;
  ratio: number;
  usdAmount?: number;
}

export enum ConfirmBotModalStep {
  EDIT_ACTIVE_WARNING,
  EDIT_OPTION,
  SELECT_ACTIVATION,
  GET_UUIDS,
  CONFIRM_AUTO_TRADING,
}

export enum CustomStrategyError {
  TARGET = "target",
  TOTAL_TARGETS = "total_targets",
  STRATEGY_NAME = "name",
}

export interface CustomStrategyErrorData {
  [CustomStrategyError.TARGET]: Record<number, string | undefined>;
  [CustomStrategyError.TOTAL_TARGETS]: string | undefined;
  [CustomStrategyError.STRATEGY_NAME]: string | undefined;
}

export interface useCustomStrategyFormProps {
  includeNameSelect?: boolean;
}

export type UseMarginForm = () => {
  longShortSelect: React.JSX.Element | undefined;
  marginTypeSelect: React.JSX.Element | undefined;
  multiplierSelect: React.JSX.Element | undefined;
  warning: React.JSX.Element | undefined;
  showLeverageSection: boolean;
  gridBotDirectionChangeModalData: {
    isOpen: boolean;
    title: string;
    content: string;
    onCancel: () => void;
    onOk: () => void;
  };
};

export interface useDCAStrategyFormProps {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  useDebounce?: boolean;
  isCustomStrategy?: boolean;
  createDcaStrategyTemplate?: ConfigurationItem;
  targets?: (number | string)[];
}

export type InitializeBaseStrategyValues = (
  numOrders: ParsedDcaStrategyData["numOrders"],
  priceDifference: ParsedDcaStrategyData["priceDifference"],
  priceScale: ParsedDcaStrategyData["priceScale"],
  numberOfActiveOrders?: ParsedDcaStrategyData["numActiveOrders"]
) => void;

export type InitializeDCAStrategyValues = (
  amountScale: ParsedDcaStrategyData["amountScale"],
  firstOrderPercentage?: ParsedDcaStrategyData["firstOrderPercent"],
  baseline?: ParsedDcaStrategyData["baseline"]
) => void;

export interface UseSharedSymbolAndAccountSelectorsProps {
  defaultExchange?: string;
  defaultAccountId?: number;
  defaultSymbols?: string[];
  defaultBlacklistSymbols?: string[];
  initialize?: boolean;
  openMessage: (text: string, type: any) => void;
  isTrade?: boolean;
  symbols?: TradeFormValues["symbols"];
  setSymbols?: (symbols: TradeFormValues["symbols"]) => void;
  account?: TradeFormValues["account"];
  setAccount?: (account?: TradeFormValues["account"]) => void;
  onSymbolChanged?: () => void;
  onAccountChanged?: () => void;
  useFreeText?: boolean;
  onChangeWithWarning?: (func: () => void) => Promise<void>;
}

export interface UseSymbolAndAccountSelectorsProps
  extends Omit<UseSharedSymbolAndAccountSelectorsProps, "openMessage"> {
  disable?: boolean;
}

export type UseSymbolAndAccountSelectors<
  T extends UseSymbolAndAccountSelectorsProps = UseSymbolAndAccountSelectorsProps
> = (props: T) => {
  symbolError: React.JSX.Element | undefined;
  accountError: React.JSX.Element | undefined;
  accountIsValid: boolean;
  symbolIsValid: boolean;
  accountSelector: React.JSX.Element | undefined;
  symbolSelector: React.JSX.Element;
  accountSelectorTitle?: string;
  symbolSelectorTitle?: string;
  noAccountModalOpen?: boolean;
  closeNoAccountModal?: () => void;
};

export interface UseSharedBotNameInputProps {
  currentBotId?: number;
  initialize?: boolean;
}

export type UseBotNameInput = (props: UseSharedBotNameInputProps) => {
  nameError: string | undefined;
  nameInput: React.JSX.Element;
};

export interface SliderAndInputProps {
  name?: string;
  value?: string;
  setValue: (value: string) => void;
  maxValue?: number;
  step?: number;
  precision?: number;
  suffix?: string;
  useDebounce?: boolean;
  helperTooltip?: React.JSX.Element;
  minValue?: number;
  showError?: boolean;
  placeholder?: string;
  isFetching?: boolean;
  wait?: number;
}

export interface TitleWithToggleProps {
  title?: ReactNode;
  content?: ReactNode;
  fieldType?: ToggleField;
  hideWithOpacity?: boolean;
  helperToolTip?: React.JSX.Element;
  size?: "default" | "small";
  onPress?: (overrideType?: OverrideType) => void;
  showMultipleValues?: boolean;
  overrideObjectName?: string;
  getDefaultValue?: () => void;
  onPersonalClick?: () => void;
  onOverrideChange?: (overrideType?: OverrideType) => void;
  defaultChannelValue?: any;
}

export interface BaselineSelectProps {
  value: OrderBaseline | undefined;
  onChange: (value: OrderBaseline) => void;
  titleText: string;
  placeholder?: string;
  titleStyle?: CSSProperties;
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP;
}

export interface SharedSummarySectionProps extends PropsWithChildren {
  errorMessage?: ReactNode;
  tooltipProps?: OnlyHelperTooltipProps;
  isFetching?: boolean;
  title?: ReactNode;
}

export interface OrdersSummaryProps {
  key: string;
  title?: string;
  isFetching: boolean;
  orders?: StrategyOrder[];
  errorMessage?: ReactNode;
  showAmount?: boolean;
  hideAmountColumn?: boolean;
  showPrice?: boolean;
  firstOrderDistanceText?: string;
  priceColumnName?: string | string[];
  amountColumnName?: string | string[];
  orderType: OrderTypes;
  botType: BotType;
  shouldDisplay: boolean;
  setErrorHeight?: (height: number) => void;
  errorsHeight?: number;
  isSideBySide?: boolean;
  sorterHeight?: number;
}

export interface OrderSummaryProps {
  order: StrategyOrder;
  index: number;
  spans: number[];
  showAmount?: boolean;
  showPrice?: boolean;
  firstOrderDistanceText?: string;
  orderType: OrderTypes;
  hideAmountColumn?: boolean;
  isVerticalCell?: boolean;
}

export type SummaryData = Omit<OrdersSummaryProps, "errorMessage"> & {
  errorMessage?: string | string[];
};

export enum FormDataType {
  ACCOUNT_AND_SYMBOL,
  SUB_GROUP,
  ALTERNATIVE_USD_PAIRS,
}

export type InitializedData = Set<FormDataType>;

export interface ParsedDcaStrategyData {
  numOrders: number | MultiValue | undefined | ChannelValue;
  numActiveOrders: number | MultiValue | undefined | ChannelValue;
  amountScale: number | MultiValue | undefined | ChannelValue;
  priceDifference: number | MultiValue | undefined | ChannelValue;
  priceScale: number | MultiValue | undefined | ChannelValue;
  firstOrderPercent:
    | number
    | undefined
    | MultiValue
    | MultiToggleValue
    | ChannelValue;
  baseline: StrategyBaseline | MultiValue | undefined | ChannelValue;
}

export interface BotFormSetters {
  setName: (name: BaseBotFormValues["name"]) => void;
  setSubGroupBotConfiguration: (
    subGroupBotConfiguration?: BaseBotFormValues["subGroupBotConfiguration"]
  ) => void;
  setSubGroup: (subGroup?: BaseBotFormValues["subGroup"]) => void;
  setSymbols: (symbols: BaseBotFormValues["symbols"]) => void;
  setIncludeNewSymbols: (
    includeNewSymbols: BaseBotFormValues["includeNewSymbols"]
  ) => void;
  setAccount: (account?: BaseBotFormValues["account"]) => void;
  setAccounts: (accounts: BaseBotFormValues["accounts"]) => void;
  setAmountType: (amountType: BaseBotFormValues["amountType"]) => void;
  setAmountValue: (amountValue: BaseBotFormValues["amountValue"]) => void;
  setAmountPercentageBaseType: (
    amountValue: BaseBotFormValues["amountPercentageBaseType"]
  ) => void;
  setAmountCustomBaseValue: (
    amountValue: BaseBotFormValues["amountCustomBaseValue"]
  ) => void;
  setLongShort: (longShort?: BaseBotFormValues["longShort"]) => void;
  setMarginType: (marginType?: BaseBotFormValues["marginType"]) => void;
  setMultiplier: (multiplier?: BaseBotFormValues["multiplier"]) => void;
  setMultiSymbolMarginData?: (
    multiSymbolMarginData?: BaseBotFormValues["multiSymbolMarginData"]
  ) => void;
  setEntryGracePercent: (
    entryGracePercent?: BaseBotFormValues["entryGracePercent"]
  ) => void;
  setEntryGraceBaseline: (
    entryGraceBaseline?: BaseBotFormValues["entryGraceBaseline"]
  ) => void;
  setTrailingStopData: (trailingStopData?: FormTrailingStopData) => void;
  setEntryStrategy: Dispatch<
    SetStateAction<BaseBotFormValues["entryStrategy"]>
  >;
  setTakeProfitStrategy: (
    takeProfitStrategy?: BaseBotFormValues["takeProfitStrategy"]
  ) => void;
  setDefaultTakeProfit: React.Dispatch<
    React.SetStateAction<BaseBotFormValues["defaultTakeProfit"]>
  >;
  setDefaultStop: React.Dispatch<
    React.SetStateAction<BaseBotFormValues["defaultStop"]>
  >;
  setTrailingEntry: (
    trailingEntry?: BaseBotFormValues["trailingEntry"]
  ) => void;
  setTrailingTakeProfit: (
    trailingTakeProfit?: BaseBotFormValues["trailingTakeProfit"]
  ) => void;
  setStopTimeout: (stopTimeout?: BaseBotFormValues["stopTimeout"]) => void;
  setNumberOfContinuousStopsTillBotStop: (
    value: BaseBotFormValues["numberOfContinuousStopsTillBotStop"]
  ) => void;
  setAdvancedSettings: (
    advancedSettings: BaseBotFormValues["advancedSettings"]
  ) => void;
  setStopType: (stopType?: BaseBotFormValues["stopType"]) => void;
  setStopLimitPriceReduction: (
    stopLimitPriceReduction?: BaseBotFormValues["stopLimitPriceReduction"]
  ) => void;
  setCancelOnProfitBeforeEntry?: (
    cancelOnProfitBeforeEntry?: BaseBotFormValues["cancelOnProfitBeforeEntry"]
  ) => void;
  setIsLeverageExchanges: (
    isLeverageExchanges: BaseBotFormValues["isLeverageExchanges"]
  ) => void;
  setCheckedFields: Dispatch<SetStateAction<Set<ToggleField>>>;
  setOverrideFields: Dispatch<SetStateAction<Set<OverrideFieldType>>>;
  setInitialized: Dispatch<SetStateAction<boolean>>;
  setGridsData: Dispatch<SetStateAction<GridsData | undefined>>;
  setSkipWithoutStopLoss: Dispatch<SetStateAction<boolean>>;
  setAlternativeUsdPairs: Dispatch<
    SetStateAction<BaseBotFormValues["alternativeUsdPairs"]>
  >;
  setTakeProfitGracePercent: Dispatch<
    SetStateAction<BaseBotFormValues["takeProfitGracePercent"]>
  >;
  setOperationHoursEvents: Dispatch<
    SetStateAction<BaseBotFormValues["operationHoursEvents"]>
  >;
}

export enum LeverageType {
  EXACT = 1,
  UP_TO = 2,
}

export type SymbolMarginData = {
  marginType: MarginTypes | ChannelValue;
  multiplier: number | ChannelValue;
  leverageType: LeverageType | ChannelValue;
};

export type MultiSymbolMarginData = {
  [symbolName: string]: SymbolMarginData;
};

export enum GridType {
  ARITHMETIC = "Arithmetic",
  GEOMETRIC = "Geometric",
  CUSTOM = "Custom",
}

export interface GridsData {
  priceRange?: {
    min?: string | undefined;
    max?: string | undefined;
  };
  numGrids?: string | undefined;
  gridType?: GridType | undefined;
}

export interface BotSubGroup extends SubGroup {
  isFreeTrialGroup?: boolean;
  groupData?: MarketplaceSubGroup;
}

export enum PercentageBaseType {
  PAIR_AMOUNT = 1,
  CUSTOM_AMOUNT = 2,
}

export interface BaseBotFormValues {
  name: string | MultiValue;
  subGroup?: BotSubGroup;
  subGroupBotConfiguration?: BotData | StrippedBotData;
  symbols: string[] | MultiValue;
  includeNewSymbols?: boolean;
  account?: ClientData | MultiValue;
  accounts: ClientData[] | MultiValue;
  amountType?: AmountType | MultiValue;
  amountValue?: number | MultiValue;
  amountPercentageBaseType?: PercentageBaseType;
  amountCustomBaseValue?: string;
  longShort?: LongShort[] | MultiValue;
  marginType?: MarginTypes | MultiValue;
  multiplier?: number | MultiValue;
  multiSymbolMarginData?: MultiSymbolMarginData | ChannelValue;
  entryGracePercent?: number | MultiValue | MultiToggleValue;
  entryGraceBaseline?: EntryGraceBaseline | null;
  trailingStopData?: FormTrailingStopData | MultiToggleValue | ChannelValue;
  entryStrategy?: StrategyData | MultiToggleValue | ChannelValue;
  takeProfitStrategy?: StrategyData | MultiToggleValue | ChannelValue;
  defaultTakeProfit?: DefaultOrder<string> | MultiToggleValue;
  defaultStop?: DefaultOrder<string> | MultiToggleValue | ChannelValue;
  trailingEntry?: number | MultiValue | MultiToggleValue | ChannelValue;
  trailingTakeProfit?: number | MultiValue | MultiToggleValue | ChannelValue;
  stopTimeout?: number | MultiValue | MultiToggleValue;
  numberOfContinuousStopsTillBotStop?: string | MultiValue;
  advancedSettings: AdvancedSettings<string | MultiValue | undefined>;
  stopType?: StopType | MultiValue;
  stopLimitPriceReduction?: string | MultiValue;
  cancelOnProfitBeforeEntry?: boolean | ChannelValue;
  gridsData?: GridsData;
  skipWithoutStopLoss: boolean;
  alternativeUsdPairs?: string[];
  isLeverageExchanges?: boolean;
  takeProfitGracePercent?: number | MultiValue | MultiToggleValue;
  operationHoursEvents?: BlacklistedOperationHours[];
}

export interface BotFormValues extends BaseBotFormValues {
  initialized: boolean;
  checkedFields: Set<ToggleField>;
  overrideFields: Set<OverrideFieldType>;
}

export type ChannelValue = symbol;
export type MultiValue = symbol;
export type MultiToggleValue = symbol;
export type Empty = typeof empty;
export type Off = typeof off;

export type BotBulkActionValidationData = {
  validationData: {
    parsedEntries?: StrategyOrder[];
    currentPrice?: number;
    parsedData?: BaseBotFormValues;
    isValid?: boolean;
  };
};

export type BulkEditBotData = BotData & BotBulkActionValidationData;
export type BotBulkActionData = BulkEditBotData | BotBulkActionValidationData;

export enum OverrideType {
  CHANNEL = "Channel",
  USER_CONFIGURATION = "User Configuration",
  OFF = "Off",
  PERSONAL = "Personal",
  DYNAMIC = "Dynamic",
  CUSTOM = "Custom",
  ALL = "All",
}

export enum RestoreChangesType {
  ORIGINAL_BOT,
  TEMPLATE,
}

export interface RestoreTabChangesData {
  valuesChanged: boolean;
  resetTab: (restoreType?: RestoreChangesType) => void;
  restoreChangesTypes: RestoreChangesType[];
}

export enum StrategyBaseline {
  FIRST_ORDER = 1,
  PREVIOUS_ORDER = 2,
}

export enum ConfirmOption {
  CREATE_ONLY,
  CREATE_AND_ACTIVATE,
}

export enum EditBotOption {
  OVERWRITE_EXISTING = 1,
  CREATE_NEW = 2,
}

export interface BotBaseStrategySelectorData {
  helperTooltipData?: OnlyHelperTooltipProps;
  placeholder?: string;
  title?: string;
  value?: string;
}

export interface MaxPriceDifferenceData {
  shouldDisplay: boolean;
  text: string;
  error?: string;
  hasConsecutiveGridsDistanceError?: boolean;
  helperData?: OnlyHelperTooltipProps;
}

export interface BotStrategySelectorData extends BotBaseStrategySelectorData {
  onChange: (value: any) => void;
  value?: string;
  rawValue?: string;
  maxValue?: any;
  step?: number;
  minValue?: number;
  showError?: boolean;
  show?: boolean;
  error?: string;
  isDefaultTakeProfit?: boolean;
  precision?: number;
  suffix?: string;
  wait?: number;
  options?: { value: number; name: string }[];
  getDefaultOverrideValue?: () => void;
  clickWarning?: string;
}

export interface BotCustomStrategyTargetsData {
  removeTarget: (index?: number) => void;
  addTarget: () => void;
  updateTarget: (targetIndex: number, value: number) => void;
  targets: CustomStrategyData["targets"];
  parsedTargets: (number | string)[] | undefined;
  disabledAdd: boolean;
  disabledRemove: boolean;
  numTargets: number;
  targetsSum: number;
  showOverrideButton: boolean;
  onOverridePress: () => void;
  setTargets: (targets: CustomStrategyData["targets"]) => void;
}

export interface useSharedDefaultOrderProps {
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP;
  addError?: (error: Error) => void;
  removeError?: (error: Error) => void;
  orders?: StrategyOrder[] | SharedOrderData[];
  checkedFields: Set<ToggleField>;
  defaultStop?: DefaultOrder<string> | MultiToggleValue | typeof channelValue;
  setDefaultStop?: (defaultStop: DefaultOrder<string>) => void;
  defaultTakeProfit?: DefaultOrder<string> | MultiToggleValue;
  setDefaultTakeProfit?: (defaultTakeProfit: DefaultOrder<string>) => void;
  longShort?: LongShort[] | MultiValue;
  botType?: BotType;
  botFormType?: BotFormType;
  onPercentInputChange?: () => void;
  onBaselineChange?: () => void;
  marginType?: MarginTypes | MultiValue;
  isMarginExchange?: boolean;
}

export interface AccountOverlapData<
  Value extends number | string = number,
  Option extends NumberOption | string = NumberOption
> {
  overlappingValues: Value[];
  optionsValues: Set<Value>;
  allOptions: Option[];
}

export interface OverlapData {
  longShort: AccountOverlapData | undefined;
  marginType: AccountOverlapData | undefined;
  multiplier: AccountOverlapData | undefined;
  symbols: AccountOverlapData<string, string> | undefined;
  stopType: AccountOverlapData | undefined;
}

export enum TargetsTabs {
  ENTRIES = "entries",
  TAKE_PROFITS = "take-profits",
}

export enum BacktestingGeneralStatistics {
  INITIAL_FUNDS = "Initial Funds",
  REALIZED_PNL = "Realized PnL",
  UNREALIZED_PNL = "Unrealized PnL",
  NET_PNL = "Net PnL",
}

export enum BacktestingPnlStatus {
  WINNING = "winning",
  LOSING = "losing",
  NEUTRAL = "neutral",
}

export enum EntryGraceBaseline {
  PRICE_REACHED = 1,
  IMMEDIATE = 2,
}

export enum BacktestActions {
  SHOW_DETAILS = "showDetails",
  APPLY_CONFIGURATION = "applyConfiguration",
  DELETE = "delete",
}

export interface SignalsMainCoinsData {
  title: string;
  sortedMainCoinsData?: CoinGroupSignalsData[];
  groupData?: GroupData;
  showEmptyState: boolean;
  emptyStateText: string;
  isFetching: boolean;
  helperData?: OnlyHelperTooltipProps;
}

export enum BotSummaryTabs {
  BACKTESTING = "Backtesting",
  PUBLIC_BOTS = "Public Bots",
}
